<template>
  <div class="index py-5">
    <div class="d-flex align-center">
      <span class="d-flex flex-row page-title">{{$t("intranet.title.page.featuredGrape.list")}}</span>
      <v-spacer></v-spacer>
      <v-btn depressed color="primary" @click="edit(null)">
        {{$t("intranet.homeContent.button.createFeaturedGrape")}}
      </v-btn>
    </div>
    <p class="text-body-2 mt-2">{{$t("intranet.homeContent.featuredGrape.intro")}}</p>

    <div class="searchable-list-container">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        disable-pagination
        hide-default-footer
      >
        <template v-slot:item.position="{ index }">{{ index + 1 }}</template>
        <template v-slot:item.grape="{ item }">{{ describeGrape(item.grape) || '—' }}</template>
        <template v-slot:item.color="{ item }">{{ colorLabel(item.color) }}</template>
        <template v-slot:item.published="{ item }">
          <v-chip small :color="item.published ? 'success' : 'grey lighten-1'">
            {{ item.published ? $t('intranet.homeContent.published') : $t('intranet.homeContent.draft') }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item, index }">
          <v-btn icon :disabled="moving || index === 0" :title="$t('intranet.homeContent.featuredGrape.moveUp')" @click="move(index, -1)">
            <v-icon>mdi-arrow-up</v-icon>
          </v-btn>
          <v-btn icon :disabled="moving || index === items.length - 1" :title="$t('intranet.homeContent.featuredGrape.moveDown')" @click="move(index, 1)">
            <v-icon>mdi-arrow-down</v-icon>
          </v-btn>
          <v-btn icon @click="edit(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon color="error" @click="askDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="formDialog" max-width="640" :persistent="saving">
      <v-card v-if="draft">
        <v-toolbar class="pl-6 text-h6" color="primary" dark flat>
          {{ draft.id ? $t('intranet.title.page.featuredGrape.update') : $t('intranet.title.page.featuredGrape.create') }}
        </v-toolbar>
        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <AveineGrapePicker
              v-model="grape"
              :label="$t('intranet.homeContent.featuredGrape.grape')"
              :APIbaseURL="APIbaseURL"
            />
            <v-select
              v-model="draft.color"
              class="mt-4"
              :items="colors"
              :label="$t('intranet.homeContent.featuredGrape.color')"
              :rules="[value => !!value]"
            />
            <v-switch v-model="draft.published" :label="$t('intranet.homeContent.attributes.published')" />
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="saving" @click="formDialog = false">{{$t('intranet.referential.button.cancel')}}</v-btn>
          <v-btn depressed color="primary" :loading="saving" :disabled="!valid || !grape" @click="save()">
            {{$t('intranet.referential.button.save')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="480">
      <v-card>
        <v-card-title>{{$t('intranet.homeContent.featuredGrape.deleteTitle')}}</v-card-title>
        <v-card-text>{{$t('intranet.homeContent.featuredGrape.deleteConfirmation')}}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">{{$t('intranet.referential.button.cancel')}}</v-btn>
          <v-btn color="error" text @click="confirmDelete()">{{$t('intranet.referential.button.delete')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FeaturedGrape from '@/store/models/featured-grape'
import AveineGrapePicker from '@/components/AveineGrapePicker.vue'
import { errorHandler } from 'aveine-toolbox'

/**
 * Les groupes que l'app affiche (rouges, blancs). La base accepte toutes les
 * couleurs de `public.wine_color` : en ajouter une ici demande que l'app
 * sache l'afficher.
 */
const COLORS = ['red', 'white']

/**
 * Les cepages proposes dans le profil de gout de l'app (REDMINE-13839), sur le
 * modele des regions du profil (featured-regions/Index.vue).
 *
 * Une seule page, dans l'ordre d'affichage de l'app, reordonnee par les
 * fleches. L'app range les cepages par couleur : celle de l'entree, pas celle
 * du cepage, que le referentiel n'a pas.
 */
export default {
  name: 'Index',
  components: { AveineGrapePicker },
  data () {
    return {
      items: [],
      loading: false,
      moving: false,
      formDialog: false,
      draft: null,
      grape: null,
      valid: false,
      saving: false,
      deleteDialog: false,
      itemToDelete: null,
      APIbaseURL: process.env.VUE_APP_API_HOST
    }
  },
  computed: {
    headers () {
      return [
        { text: '#', value: 'position', sortable: false, width: 60 },
        { text: this.$t('intranet.homeContent.featuredGrape.grape'), value: 'grape', sortable: false },
        { text: this.$t('intranet.homeContent.featuredGrape.color'), value: 'color', sortable: false },
        { text: this.$t('intranet.homeContent.attributes.published'), value: 'published', sortable: false, width: 140 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 220 }
      ]
    },
    colors () {
      return COLORS.map(value => ({ value, text: this.colorLabel(value) }))
    }
  },
  mounted () {
    this.fetch()
  },
  methods: {
    describeGrape (grape) {
      return AveineGrapePicker.describe(grape)
    },

    colorLabel (color) {
      return color ? this.$t(`intranet.homeContent.featuredGrape.colors.${color}`) : '—'
    },

    async fetch () {
      this.loading = true
      try {
        const response = await FeaturedGrape.api().get(
          'featured-grapes',
          {
            baseURL: this.APIbaseURL,
            params: { include: 'grape.grape_variety', sort: 'position', 'page[size]': 100 }
          }
        )
        // Restreint aux identifiants recus, dans l'ordre du serveur : voir
        // wines-of-the-month/Index.vue.
        const ids = response.entities ? (response.entities.featured_grapes || []).map(item => item.id) : []
        const byId = new Map(
          FeaturedGrape.query().whereIdIn(ids).withAllRecursive().get().map(item => [item.id, item])
        )
        this.items = ids.map(id => byId.get(id)).filter(Boolean)
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    },

    edit (item) {
      this.draft = item
        ? { id: item.id, color: item.color, published: item.published }
        : { id: null, color: null, published: false }
      this.grape = item && item.grape
        ? { id: item.grape.id, label: this.describeGrape(item.grape) }
        : null
      this.formDialog = true
    },

    async save () {
      this.saving = true
      try {
        // `$entity_name` sur la ressource et sur le lien : c'est de la que le
        // dataTree tire les types JSON:API (voir AveineWineOfTheMonthForm).
        const payload = {
          $entity_name: FeaturedGrape.entity,
          color: this.draft.color,
          published: this.draft.published,
          grape: { $entity_name: 'grapes', id: this.grape.id },
          grape_id: this.grape.id
        }
        if (this.draft.id) {
          await FeaturedGrape.api().patch(
            `featured-grapes/${this.draft.id}`,
            { ...payload, id: this.draft.id },
            { baseURL: this.APIbaseURL, save: false }
          )
        } else {
          // En fin de liste.
          const last = this.items.reduce((max, item) => Math.max(max, item.position || 0), 0)
          await FeaturedGrape.api().post(
            'featured-grapes',
            { ...payload, position: last + 1 },
            { baseURL: this.APIbaseURL, save: false }
          )
        }
        this.formDialog = false
        this.$toastr.s(this.$t('intranet.referential.title.saveConfirmation'))
        await this.fetch()
      } catch (errors) {
        if (this.isGrapeTaken(errors)) {
          this.$toastr.e(this.$t('intranet.homeContent.featuredGrape.grapeTaken'))
        } else {
          errorHandler(errors, this)
        }
      } finally {
        this.saving = false
      }
    },

    /** Le refus de CRUD-Wine quand le cepage est deja propose. */
    isGrapeTaken (errors) {
      const body = errors && errors.response && errors.response.data
      if (!body || !Array.isArray(body.errors)) return false
      return body.errors.some(error => /already featured|featured_grapes_grape_unique/.test(error.detail || ''))
    },

    /**
     * Echange deux entrees voisines, en renumerotant les positions de 1 a n :
     * voir featured-regions/Index.vue.
     */
    async move (index, step) {
      const order = this.items.slice()
      const [item] = order.splice(index, 1)
      order.splice(index + step, 0, item)

      this.moving = true
      try {
        for (const [position, entry] of order.entries()) {
          if (entry.position === position + 1) continue
          await FeaturedGrape.api().patch(
            `featured-grapes/${entry.id}`,
            { $entity_name: FeaturedGrape.entity, id: entry.id, position: position + 1 },
            { baseURL: this.APIbaseURL, save: false }
          )
        }
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.moving = false
        await this.fetch()
      }
    },

    askDelete (item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete () {
      try {
        await FeaturedGrape.api().delete(
          `featured-grapes/${this.itemToDelete.id}`,
          { baseURL: this.APIbaseURL, save: false }
        )
        FeaturedGrape.delete(this.itemToDelete.id)
        this.deleteDialog = false
        this.itemToDelete = null
        await this.fetch()
      } catch (errors) {
        this.deleteDialog = false
        errorHandler(errors, this)
      }
    }
  }
}
</script>
